import {
    faCheckCircle,
    faChevronDown,
    faChevronUp,
    faCircle,
    faHospital,
    faHourglassStart,
    faL,
    faRefresh
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { callDashboardLLM, callGetDashboardLLMStatus } from "../../../amplify/api-gateway/dashboard-llm";
import { callQuery, LLM_VISIT_FEEDBACK_BY_CSN, LLMVisitFeedback, PatientFileVersion } from "../../../amplify/api-gateway/query";
import { PatientVisit } from "../../../amplify/api-gateway/search-patients";
import { callWrite, EXPLAIN_REVIEW_EDD, markJobAsViewed, SCAN_EDD, UPDATE_LLM_VISIT_FEEDBACK } from "../../../amplify/api-gateway/write";
import { GenerateModuleHeroV2 } from "../../common/GenerateModuleHeroV2";
import { dashboardLLMCache } from "../../dashboard/llm/cache";
import { FakeLoadingBar } from "../../dashboard/llm/common/FakeLoadingBar";
import { LLMDashboardUpdated } from "../../dashboard/llm/common/LLMDashboardUpdated";
import { sleep } from "../../dashboard/llm/common/utils";
import { isSectionUnread } from "../../dashboard/llm/utils";
import { DISPOSITION_MAPPING } from "../../ops/metricsPlotOpts";
import CustomSurveyForm, { getCombinedSectionToRate } from "./CustomSurveyForm";
import { NotableLabelsBadge } from "../../common/NotableLabelsBadge";
import { getNotableLabels, isFieldValueNotable } from "../../../utils/notableLabels";

interface DashboardProps {
    patientVisit?: PatientVisit;
    statuses: any;
    setStatuses: any;
    latestFileVersions: any;
    handleFileVersionChange: any;
    unreadFileVersionMap?: Record<string, PatientFileVersion>;
    className?: string;
    isQuickView?: boolean;
    tenant?: string;
    surveyResultsUpdateCallback?: (csn: string, results: Record<string, any>) => void;
    notableLabelsUpdateCallback?: (csn: string, surveyType: string, count: number) => void;
}

const EXPECTED_REFRESH_TIME = 120_000;

export const FEEDBACK_TYPE_INDEX_SUMMARY = "cohort_edd_index_summary";
export const FEEDBACK_TYPE_PATIENT_SUMMARY_EDD = "cohort_edd_patient_summary_edd";
export const FEEDBACK_TYPE_POST_DISCHARGE_MONITORING = "cohort_edd_post_discharge_monitoring";
export const FEEDBACK_TYPE_POST_DISCHARGE_MONITORING_REASONING = "cohort_edd_reasoning_pdm";
export const FEEDBACK_TYPE_NYU_CLASSIFICATION = "cohort_edd_nyu_classification";
export const FEEDBACK_TYPE_NYU_REASONING = "cohort_edd_reasoning_nyu";
export const FEEDBACK_TYPE_LONGITUDINAL_CARE_INSIGHT_EDD = "cohort_edd_longitudinal_care_insight_edd";
export const FEEDBACK_TYPE_THERAPEUTIC_PIVOT_EDD = "cohort_edd_therapeutic_pivot_edd";
export const FEEDBACK_TYPE_THERAPEUTIC_PIVOT_JUSTIFICATION_EDD = "cohort_edd_therapeutic_pivot_justification_edd";
export const FEEDBACK_TYPE_VALUE_OF_CARE_EDD = "cohort_edd_value_of_care_edd";
export const FEEDBACK_TYPE_VALUE_OF_CARE_JUSTIFICATION_EDD = "cohort_edd_value_of_care_justification_edd";
export const FEEDBACK_TYPE_ASTUTE_DIAGNOSIS_EDD = "cohort_edd_astute_diagnosis_edd";
export const FEEDBACK_TYPE_ASTUTE_DIAGNOSIS_JUSTIFICATION_EDD = "cohort_edd_astute_diagnosis_justification_edd";

const REQUIRED_FEEDBACK_TYPES = [
    FEEDBACK_TYPE_INDEX_SUMMARY,
    FEEDBACK_TYPE_PATIENT_SUMMARY_EDD,
    FEEDBACK_TYPE_POST_DISCHARGE_MONITORING,
    FEEDBACK_TYPE_POST_DISCHARGE_MONITORING_REASONING,
    FEEDBACK_TYPE_NYU_CLASSIFICATION,
    FEEDBACK_TYPE_NYU_REASONING,
    FEEDBACK_TYPE_LONGITUDINAL_CARE_INSIGHT_EDD,
    FEEDBACK_TYPE_THERAPEUTIC_PIVOT_EDD,
    FEEDBACK_TYPE_THERAPEUTIC_PIVOT_JUSTIFICATION_EDD,
    FEEDBACK_TYPE_VALUE_OF_CARE_EDD,
    FEEDBACK_TYPE_VALUE_OF_CARE_JUSTIFICATION_EDD,
    FEEDBACK_TYPE_ASTUTE_DIAGNOSIS_EDD,
    FEEDBACK_TYPE_ASTUTE_DIAGNOSIS_JUSTIFICATION_EDD,
] as const;

const isSurveyCompleted = (feedbackTargetToSurvey: any) => {
    return REQUIRED_FEEDBACK_TYPES.every(type => feedbackTargetToSurvey[type]);
};


export const ExplainEDDDashboard = ({
    patientVisit,
    statuses,
    setStatuses,
    unreadFileVersionMap = undefined,
    latestFileVersions = undefined,
    handleFileVersionChange = undefined,
    className,
    isQuickView,
    tenant = 'stanford',
    surveyResultsUpdateCallback,
    notableLabelsUpdateCallback,
}: DashboardProps) => {

    const [isLoading, setIsLoading] = useState(false);
    const [scanEDDData, setScanEDDData] = useState<any>();
    const [explainReviewEDD, setExplainReviewEDD] = useState<any>();
    const [feedbackTargetToSurvey, setFeedbackTargetToSurvey] = useState<any>({});
    const [errorMessage, setErrorMessage] = useState("");
    const [isExpanded, setIsExpanded] = useState(true);
    const [manuallyChangedIsExpanded, setManuallyChangedIsExpanded] = useState(false);

    const sectionToSetter = {
        [EXPLAIN_REVIEW_EDD]: setExplainReviewEDD,
    }

    // Function to fetch dashboard data
    const fetchDashboardData = async (patient: PatientVisit) => {
        setIsLoading(true);
        const decoder = new TextDecoder();
        
        try {
            // We will render the data just like any other dashboard
            const cachedScanData = await dashboardLLMCache.get(SCAN_EDD, patient, tenant);
            if (cachedScanData) {
                setScanEDDData(cachedScanData);
            } else {
                setScanEDDData(undefined);
            }

            const cachedData = await dashboardLLMCache.get(EXPLAIN_REVIEW_EDD, patient, tenant);
            if (cachedData) {
                // Found cached data
                setExplainReviewEDD(cachedData);
                markJobAsViewed(EXPLAIN_REVIEW_EDD, patientVisit);
                // Update notable labels count
                if (notableLabelsUpdateCallback && patientVisit) {
                    const notableCount = getNotableLabels('explain_review_edd', cachedData).length;
                    notableLabelsUpdateCallback(patientVisit.csn, 'explain_review_edd', notableCount);
                }
            } else {
                setExplainReviewEDD(undefined);
            }
            setManuallyChangedIsExpanded(false);

            // Fetch survey data
            await fetchSurveyData();
            
        } catch (error) {
            console.error("Error fetching data:", error);
            setErrorMessage("Could not load data. Check that you are on the Stanford network and if using VPN that your VPN Group is set to either 'CardinalKey-Full' or 'Stanford-Full'");
            setScanEDDData(undefined);
            setExplainReviewEDD(undefined);
        } finally {
            setIsLoading(false);
        }
    };
    
    // Function to fetch survey data
    const fetchSurveyData = async () => {
        if (!patientVisit) {
            return;
        }

        try {
            console.log("Fetching survey data for CSN:", patientVisit.csn);
            const feedbackItems = await callQuery({
                operation: LLM_VISIT_FEEDBACK_BY_CSN,
                csn: patientVisit.csn,
            }) as LLMVisitFeedback[];
            
            console.log("Survey data result:", feedbackItems);
            
            if (feedbackItems && feedbackItems.length > 0) {
                // Get all original survey feedback items
                const originalSurveyFeedbackItems = feedbackItems.filter(
                    (item: LLMVisitFeedback) => 
                        item.feedback_type === 'survey' && 
                        (item.feedback_target.startsWith("cohort_edd_"))
                );

                const feedbackTargetToSurvey: any = {};

                for (let item of originalSurveyFeedbackItems) {
                    const survey = JSON.parse(item.note);
                    feedbackTargetToSurvey[item.feedback_target] = {
                        "option": survey.option,
                        "options": survey.options,
                        "comment": survey.comment,
                    };
                }

                if (surveyResultsUpdateCallback) {
                    surveyResultsUpdateCallback(patientVisit.csn, feedbackTargetToSurvey);
                }

                setFeedbackTargetToSurvey(feedbackTargetToSurvey);
            } else {
                setFeedbackTargetToSurvey({});
            }
        } catch (error) {
            console.error("Error fetching survey data:", error);
        }
    };

    const handleSave = async (feedback_target: string, data: any): Promise<boolean> => {
        if (!patientVisit) {
            return false;
        }

        // Set this flag to prevent section from collapsing when user finishes survey
        setManuallyChangedIsExpanded(true);

        try {
            const response = await callWrite({
                operation: UPDATE_LLM_VISIT_FEEDBACK,
                creation_time: new Date().getTime(),
                csn: patientVisit.csn,
                mrn: '',
                feedback_target: feedback_target,
                feedback_type: 'survey',
                note: JSON.stringify(data),
            });
            
            // Check if response is empty or has an error
            if (!response || Object.keys(response).length === 0) {
                window.alert("Failed to submit survey. Please refresh and try again.");
                return false;
            }

            // Refresh the survey data after submission
            await fetchSurveyData();
            
            return true;
        } catch (error) {
            console.error("Error submitting original survey:", error);
            
            // Show alert with error details
            if (error instanceof Error) {
                window.alert(`Failed to submit survey: ${error.message}`);
            } else {
                window.alert("Failed to submit survey. An unknown error occurred.");
            }

            return false;
        }
    };

    const getIsCompleted = () => {
        const isCompleted = isSurveyCompleted(feedbackTargetToSurvey);

        return (
            <Row>
                <Col>
                    {
                        isCompleted ? (
                            <Alert variant="success">
                                <span className="me-1"><FontAwesomeIcon icon={faCheckCircle} /></span> Survey Completed
                            </Alert>
                        ) : (
                            <Alert variant="info">
                                <span className="me-1"><FontAwesomeIcon icon={faHourglassStart} /></span> Survey Not Yet Completed
                            </Alert>
                        )
                    }
                </Col>
            </Row>
        );
    };

    const getSectionHeader = (title: string) => {
        return (
            <Row>
                <Col>
                    <div className="survey-section p-3 mb-4">
                        {title}
                    </div>
                </Col>
            </Row>
        );
    };

    const getContextSection = (title: string, data: string | string[], horizontalLineAbove: boolean = true) => {
        return (
            <>
                {horizontalLineAbove && <hr />}
                <Row className="mb-2">
                    <Col>
                        <strong>{title}</strong>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        {Array.isArray(data) ? (
                            <div className="survey-context">
                                <ul>
                                    {data.map((item, index) => (
                                        <li key={index}>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <div className="survey-context">
                                {data}
                            </div>
                        )}
                    </Col>
                </Row>
            </>
        );
    }

    const getSectionToRate = (title: string, data: string, surveyTitle: string, 
        surveyDescription: string, surveyOptions: string[], 
        surveyKey: string, isMultiSelect?: boolean, optionToValueMap?: any) => {
        return (
            <>
                <hr />
                <Row>
                    <Col>
                        <div>
                            <strong>{title}</strong>
                        </div>
                        {Array.isArray(data) ? (
                            <div className="survey-context">
                                <ul>
                                    {data.map((item, index) => (
                                        <li key={index}>
                                            {(optionToValueMap && optionToValueMap[item]) ? optionToValueMap[item] : item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <div className="survey-context">
                                {(optionToValueMap && optionToValueMap[data]) ? optionToValueMap[data] : data}
                            </div>
                        )}
                    </Col>
                    <Col>
                        <CustomSurveyForm
                            title={surveyTitle}
                            altTitle={title}
                            description={surveyDescription}
                            options={surveyOptions}
                            onSubmit={(data) => handleSave(surveyKey, data)}
                            enableComment={true}
                            initialSelectedOption={feedbackTargetToSurvey[surveyKey] ? feedbackTargetToSurvey[surveyKey].option : undefined}
                            initialSelectedOptions={feedbackTargetToSurvey[surveyKey] ? feedbackTargetToSurvey[surveyKey].options : undefined}
                            initialComment={feedbackTargetToSurvey[surveyKey] ? feedbackTargetToSurvey[surveyKey].comment : undefined}
                            isMultiSelect={isMultiSelect}
                        />
                    </Col>
                </Row>
            </>
        )
    }

    const flattenAndFormatNotes = (notes: any) => {
        let output = "";
        if (notes) {
            for (let k of Object.keys(notes)) {
                for (let note of notes[k]) {
                    output += `NOTE ${(note.note_text)}\n`
                    output += note.note_text + "\n\n";
                }
            }
        }
        return output;
    }

    // Update useEffect to also fetch patient visit data
    useEffect(() => {
        if (patientVisit) {
            fetchDashboardData(patientVisit);
        }
    }, [patientVisit, tenant]);

    useEffect(() => {
        if (isQuickView && !manuallyChangedIsExpanded) {
            if (isSurveyCompleted(feedbackTargetToSurvey)) {
                setIsExpanded(false);
            } else {
                setIsExpanded(true);
            }
        }
    }, [feedbackTargetToSurvey]);

    const refresh = (refresh: boolean) => {
        if (!patientVisit) {
            return;
        }
        setIsLoading(true);
        callDashboardLLM({
            "operation": SCAN_EDD,
            "mrn": patientVisit.mrn,
            "csn": patientVisit.csn,
        }).then((scanRevisitResponse) => {
            setScanEDDData(scanRevisitResponse);

            const startTime = new Date().toISOString().split('.')[0] + 'Z';
            callWrite({
                operation: EXPLAIN_REVIEW_EDD,
                mrn: patientVisit.mrn,
                csn: patientVisit.csn,
                report_date_str: new Date().toISOString().split('.')[0] + 'Z',
            }).then((reviewRevisitResponse) => {
                pollUntilDone({
                    operation: EXPLAIN_REVIEW_EDD, 
                    startTime,
                });
            }).catch(() => {
                setIsLoading(false);
            });
        }).catch(() => {
            setIsLoading(false);
        });
    };

    const pollUntilDone = async ({
        operation,
        startTime,
        delayMs = 2000,
        maxAttempts = 60,
    }: any) => {
        if (patientVisit) {
            for (let attempt = 0; attempt < maxAttempts; attempt++) {
                const statuses = await callGetDashboardLLMStatus(patientVisit.csn);
                if (statuses[operation] && statuses[operation].lock_timestamp === null && statuses[operation].last_sync_timestamp > startTime) {
                    const cachedCurrentVisit = await dashboardLLMCache.get(operation, patientVisit, tenant);
                    if (cachedCurrentVisit) {
                        setExplainReviewEDD(cachedCurrentVisit);
                        markJobAsViewed(EXPLAIN_REVIEW_EDD, patientVisit);
                    }

                    const cachedScanData = await dashboardLLMCache.get(SCAN_EDD, patientVisit, tenant);
                    if (cachedScanData) {
                        setScanEDDData(cachedScanData);
                    }
                    setIsLoading(false);
                    setStatuses(statuses);
                    return;
                }

                await sleep(delayMs);
            }
            setIsLoading(false);
            console.error('Polling timed out');
        }
    };

    if (!patientVisit) {
        return <></>
    }

    if (isQuickView && (!scanEDDData || Object.keys(scanEDDData).length === 0 || !explainReviewEDD || Object.keys(explainReviewEDD).length === 0)) {
        return <></>
    }

    function checkVisitCriteria(): { result: boolean; reason?: string } {
        // Check if target visit has ed_dispo set
        const dispo = patientVisit?.ed_dispo;
        if (!dispo) {
            return { result: false, reason: "Patient visit does not have a disposition status." };
        }
        if (!DISPOSITION_MAPPING[dispo]) {
            return { result: false, reason: "Patient visit has an unknown disposition status." };
        }
        if (DISPOSITION_MAPPING[dispo] !== "Discharge") {
            return { result: false, reason: "Patient was not discharged." };
        }

        return { result: true };
    }

    const criteria = checkVisitCriteria();
    if (!isQuickView && !criteria.result) {
        return (
            <Card className="mb-3">
                <Card.Body>
                    <Row className="mb-3">
                        <Col md={12} className="text-left">
                            <div className="d-flex align-items-center">
                                <span className={`dashboard-actions-circle-small dashboard-actions-circle-blue me-2`}><FontAwesomeIcon icon={faHospital} fixedWidth /></span>

                                <strong>Discharge Assessment</strong>
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            This patient is ineligible for review/assessment - {criteria.reason}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        )
    }

    if (!isLoading && (!explainReviewEDD || explainReviewEDD["error"])) {
        return (
            <>
                <GenerateModuleHeroV2 type={EXPLAIN_REVIEW_EDD} setRegenerateTarget={() => {}} setShowContextMenu={refresh} />
            </>
        )
    }

    const missingItems = [];

    if (!scanEDDData) {
        missingItems.push("Review Data");
    } else {
        if (!scanEDDData.index_formatted_notes?.length) {
            missingItems.push("Provider Note");
        }
        if (!scanEDDData.index_dc_note?.note_text) {
            missingItems.push("Discharge Instructions");
        }
        if (!scanEDDData.dx_index || !Object.keys(scanEDDData.dx_index).length) {
            missingItems.push("Diagnoses");
        }
    }

    return (
        <Card className={`fade-in ${className} mb-3`}>
            <FakeLoadingBar isActive={isLoading} maxDuration={EXPECTED_REFRESH_TIME} />
            <Card.Body>
                <Row>
                    <Col md={6}>
                        <div className="d-flex align-items-center">
                            <span className={`dashboard-actions-circle-small dashboard-actions-circle-blue me-2`}><FontAwesomeIcon icon={faHospital} fixedWidth /></span><strong>ED Discharge Assessment</strong> {isSectionUnread(EXPLAIN_REVIEW_EDD, unreadFileVersionMap) && <Badge bg="info" className="ms-2">NEW</Badge>} {isSurveyCompleted(feedbackTargetToSurvey) && <Badge bg="success" className="ms-2">COMPLETED</Badge>} {explainReviewEDD && <NotableLabelsBadge count={getNotableLabels('explain_review_edd', explainReviewEDD).length} labels={getNotableLabels('explain_review_edd', explainReviewEDD)} />}
                        </div>
                    </Col>
                    <Col md={6} className="text-right">
                        {patientVisit && <LLMDashboardUpdated patient={patientVisit} statuses={statuses} sectionToSetter={sectionToSetter} latestFileVersions={latestFileVersions} explainKey={EXPLAIN_REVIEW_EDD} handleFileVersionChange={handleFileVersionChange} />}

                        <OverlayTrigger
                            placement="bottom"
                            overlay={
                                <Tooltip>
                                    Refresh Section
                                </Tooltip>
                            }
                        >
                            <Button
                                variant="primary"
                                className="dashboard-round-btn"
                                onClick={(e: any) => {
                                    e.stopPropagation();
                                    refresh(true);
                                }}
                                disabled={isLoading}
                            >
                                <FontAwesomeIcon icon={faRefresh} />
                            </Button>
                        </OverlayTrigger>
                        <Button
                            variant="primary"
                            className="dashboard-round-btn"
                            onClick={(e: any) => {e.stopPropagation(); setIsExpanded(!isExpanded); setManuallyChangedIsExpanded(true)}}
                        >
                            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                        </Button>
                    </Col>
                </Row>
                {isLoading && (
                        <div className='mt-2'>
                            <FontAwesomeIcon icon={faCircle} className="pulsating-dot" />
                        </div>
                    )
                }
                {!isLoading && isExpanded && scanEDDData && explainReviewEDD && scanEDDData.index_formatted_notes && explainReviewEDD.index_summary ? (
                    <Container>
                        {getIsCompleted()}
                        {getContextSection("Provider Note", flattenAndFormatNotes(scanEDDData.index_formatted_notes), false)}
                        {getContextSection("Discharge Instructions", scanEDDData.index_dc_note?.note_text)}
                        {getContextSection("Diagnoses", JSON.stringify(scanEDDData.dx_index))}

                        {getSectionToRate("Index Summary", explainReviewEDD.index_summary, "Index Summary Accuracy", 
                            "Please rate the accuracy of this generated summary.", 
                            ['Accurate', 'Partially accurate', 'Inaccurate'], FEEDBACK_TYPE_INDEX_SUMMARY)}

                        {getSectionToRate("Patient Summary", explainReviewEDD.patient_summary_edd, "Patient Summary Quality", 
                            "Please rate the quality of this patient-facing summary (written at 6th-8th grade reading level).", 
                            ['Good', 'Incomplete', 'Poor'], FEEDBACK_TYPE_PATIENT_SUMMARY_EDD)}

                        {getCombinedSectionToRate(handleSave, feedbackTargetToSurvey,
                            "Post-Discharge Monitoring",
                            explainReviewEDD.post_discharge_monitoring,
                            explainReviewEDD.reasoning_pdm,
                            false,
                            "Select applicable category:",
                            [
                                'Likely',
                                'Possibly',
                                'Unlikely',
                            ],
                            FEEDBACK_TYPE_POST_DISCHARGE_MONITORING,
                            "Reasoning Quality",
                            "Rate the quality of the reasoning for the post-discharge monitoring assessment.",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_POST_DISCHARGE_MONITORING_REASONING,
                            false,
                            {
                                'Likely': 'Likely',
                                'Possibly': 'Possibly',
                                'Unlikely': 'Unlikely',
                            },
                            isFieldValueNotable('explain_review_edd', 'post_discharge_monitoring', explainReviewEDD.post_discharge_monitoring)
                        )}

                        {getCombinedSectionToRate(handleSave, feedbackTargetToSurvey,
                            "NYU Classification", 
                            explainReviewEDD.nyu_classification,
                            explainReviewEDD.reasoning_nyu,
                            true,
                            "Please select the most appropriate NYU ED visit classification for this revisit.", 
                            [
                                'Non-Emergent',
                                'Emergent Primary Care Treatable',
                                'Emergent ED Care Needed (Preventable)',
                                'Emergent ED Care Needed (Non-Preventable)',
                            ],
                            FEEDBACK_TYPE_NYU_CLASSIFICATION,
                            "Reasoning Quality",
                            "Please rate the quality of the reasoning provided for why this NYU classification was chosen.",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_NYU_REASONING,
                            false,
                            {
                                'non_emergent': 'Non-Emergent',
                                'emergent_primary_care_treatable': 'Emergent Primary Care Treatable',
                                'emergent_ed_care_needed_preventable_avoidable': 'Emergent ED Care Needed (Preventable)',
                                'emergent_ed_care_needed_not_preventable_avoidable': 'Emergent ED Care Needed (Non-Preventable)',
                            }
                        )}

                        {getSectionToRate("Longitudinal Care Insight", explainReviewEDD.longitudinal_care_insight_edd, "Clinical Pearl Quality", 
                            "Rate the quality and usefulness of this clinical pearl for future care.", 
                            ['Very useful', 'Somewhat useful', 'Not useful'], FEEDBACK_TYPE_LONGITUDINAL_CARE_INSIGHT_EDD)}

                        {getCombinedSectionToRate(handleSave, feedbackTargetToSurvey,
                            "Therapeutic Pivot",
                            explainReviewEDD.therapeutic_pivot_edd,
                            explainReviewEDD.therapeutic_pivot_justification_edd,
                            false,
                            "Select an applicable category for whether there was a significant change in treatment plan:", 
                            [
                                'Yes',
                                'Possibly',
                                'No',
                            ], 
                            FEEDBACK_TYPE_THERAPEUTIC_PIVOT_EDD,
                            "Pivot Justification Quality",
                            "Rate the quality of the therapeutic pivot description.",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_THERAPEUTIC_PIVOT_JUSTIFICATION_EDD,
                            false,
                            {
                                'yes': 'Yes',
                                'possibly': 'Possibly',
                                'no': 'No',
                            }
                        )}

                        {getCombinedSectionToRate(handleSave, feedbackTargetToSurvey,
                            "Value of Care",
                            explainReviewEDD.value_of_care_edd,
                            explainReviewEDD.value_of_care_justification_edd,
                            false,
                            "Select an applicable category for the value added by this visit:",
                            [
                                'High',
                                'Moderate',
                                'Low',
                                'Negative',
                            ],
                            FEEDBACK_TYPE_VALUE_OF_CARE_EDD,
                            "Value Justification Quality",
                            "Rate the quality of the value of care justification.",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_VALUE_OF_CARE_JUSTIFICATION_EDD,
                            false,
                            {
                                'high': 'High',
                                'moderate': 'Moderate',
                                'low': 'Low',
                                'negative': 'Negative',
                            },
                            isFieldValueNotable('explain_review_edd', 'value_of_care_edd', explainReviewEDD.value_of_care_edd)
                        )}

                        {getCombinedSectionToRate(handleSave, feedbackTargetToSurvey,
                            "Astute Diagnosis",
                            explainReviewEDD.astute_diagnosis_edd,
                            explainReviewEDD.astute_diagnosis_justification_edd,
                            false,
                            "Select an applicable category for whether an astute diagnosis was made:", 
                            [
                                'Yes',
                                'Possibly',
                                'No',
                            ], 
                            FEEDBACK_TYPE_ASTUTE_DIAGNOSIS_EDD,
                            "Diagnosis Justification Quality",
                            "Rate the quality of the astute diagnosis justification.",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_ASTUTE_DIAGNOSIS_JUSTIFICATION_EDD,
                            false,
                            {
                                'yes': 'Yes',
                                'possibly': 'Possibly',
                                'no': 'No',
                            }
                        )}

                        {getSectionToRate(
                            "Value of Care",
                            explainReviewEDD.value_of_care_edd,
                            "Value of Care Assessment", 
                            "Select an applicable category for whether there was a significant change in treatment plan:", 
                            [
                                'High',
                                'Moderate',
                                'Low',
                                'Negative',
                            ], 
                            FEEDBACK_TYPE_VALUE_OF_CARE_EDD,
                            false,
                            {
                                'high': 'High',
                                'moderate': 'Moderate',
                                'low': 'Low',
                                'negative': 'Negative',
                            }
                        )}

                        {getSectionToRate("Value of Care Justification", explainReviewEDD.value_of_care_justification_edd, "Value Justification Quality", 
                            "Rate the quality of the value of care justification.", 
                            ['Good', 'Incomplete', 'Poor'], FEEDBACK_TYPE_VALUE_OF_CARE_JUSTIFICATION_EDD)}

                        {getSectionToRate(
                            "Astute Diagnosis",
                            explainReviewEDD.astute_diagnosis_edd,
                            "Astute Diagnosis Assessment", 
                            "Select an applicable category for whether an astute diagnosis was made:", 
                            [
                                'Yes',
                                'Possibly',
                                'No',
                            ], 
                            FEEDBACK_TYPE_ASTUTE_DIAGNOSIS_EDD,
                            false,
                            {
                                'yes': 'Yes',
                                'possibly': 'Possibly',
                                'no': 'No',
                            }
                        )}

                        {getSectionToRate("Astute Diagnosis Justification", explainReviewEDD.astute_diagnosis_justification_edd, "Diagnosis Justification Quality", 
                            "Rate the quality of the astute diagnosis justification.", 
                            ['Good', 'Incomplete', 'Poor'], FEEDBACK_TYPE_ASTUTE_DIAGNOSIS_JUSTIFICATION_EDD)}

                    </Container>
                ) : !isLoading && isExpanded && missingItems.length > 0 ? (
                    <>We couldn't generate an assessment because the following information is missing: <strong>{missingItems.join(", ")}</strong></>
                ) : !isLoading && isExpanded && scanEDDData && explainReviewEDD ? (
                    <>We're sorry - we could not generate an assessment due to an error</>
                ) : (<></>)}
            </Card.Body>
        </Card>
    );
};
